import { useState, useEffect } from 'react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircle, XCircle, AlertCircle, ChevronUp } from 'lucide-react';

interface DiagnosticCheck {
  name: string;
  status: 'ok' | 'error' | 'warning';
  message: string;
}

export function DevDiagnosticBadge() {
  const [isOpen, setIsOpen] = useState(false);
  const [checks, setChecks] = useState<DiagnosticCheck[]>([]);
  const [healthData, setHealthData] = useState<any>(null);

  // 개발 모드에서만 표시
  const isDev = import.meta.env.DEV || import.meta.env.MODE === 'development';

  useEffect(() => {
    if (!isDev) return;

    const runDiagnostics = async () => {
      const diagnostics: DiagnosticCheck[] = [];

      // 1. VITE_ 환경변수 체크
      const requiredVars = [
        'VITE_FIREBASE_API_KEY',
        'VITE_KAKAO_JAVASCRIPT_KEY',
        'VITE_GOOGLE_MAPS_API_KEY',
      ];

      requiredVars.forEach(varName => {
        const exists = !!import.meta.env[varName];
        diagnostics.push({
          name: varName,
          status: exists ? 'ok' : 'error',
          message: exists ? 'Configured' : 'Missing',
        });
      });

      // 2. Kakao SDK 체크
      if (window.Kakao) {
        diagnostics.push({
          name: 'Kakao SDK',
          status: window.Kakao.isInitialized() ? 'ok' : 'warning',
          message: window.Kakao.isInitialized() ? 'Initialized' : 'Not initialized',
        });
      } else {
        diagnostics.push({
          name: 'Kakao SDK',
          status: 'error',
          message: 'Not loaded',
        });
      }

      // 3. Geolocation API 체크
      if ('geolocation' in navigator) {
        try {
          await navigator.permissions.query({ name: 'geolocation' as PermissionName });
          diagnostics.push({
            name: 'Geolocation',
            status: 'ok',
            message: 'Available',
          });
        } catch {
          diagnostics.push({
            name: 'Geolocation',
            status: 'warning',
            message: 'Permission not granted',
          });
        }
      } else {
        diagnostics.push({
          name: 'Geolocation',
          status: 'error',
          message: 'Not supported',
        });
      }

      // 4. Health 엔드포인트 체크
      try {
        const response = await fetch('/api/health');
        if (response.ok) {
          const data = await response.json();
          setHealthData(data);
          diagnostics.push({
            name: '/api/health',
            status: 'ok',
            message: `OK (${response.status})`,
          });
        } else {
          diagnostics.push({
            name: '/api/health',
            status: 'error',
            message: `Failed (${response.status})`,
          });
        }
      } catch (error) {
        diagnostics.push({
          name: '/api/health',
          status: 'error',
          message: 'Network error',
        });
      }

      // 5. Articles API 체크
      try {
        const response = await fetch('/api/articles?limit=1');
        if (response.ok) {
          const data = await response.json();
          const count = Array.isArray(data) ? data.length : 0;
          diagnostics.push({
            name: '/api/articles',
            status: count > 0 ? 'ok' : 'warning',
            message: count > 0 ? `${count} items` : 'No data',
          });
        } else {
          diagnostics.push({
            name: '/api/articles',
            status: 'error',
            message: `Failed (${response.status})`,
          });
        }
      } catch (error) {
        diagnostics.push({
          name: '/api/articles',
          status: 'error',
          message: 'Network error',
        });
      }

      setChecks(diagnostics);
    };

    runDiagnostics();
  }, [isDev]);

  if (!isDev) return null;

  const errorCount = checks.filter(c => c.status === 'error').length;
  const warningCount = checks.filter(c => c.status === 'warning').length;
  const okCount = checks.filter(c => c.status === 'ok').length;

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'ok':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'error':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'warning':
        return <AlertCircle className="w-4 h-4 text-yellow-500" />;
      default:
        return null;
    }
  };

  return (
    <div className="fixed top-4 right-4 z-50" data-testid="dev-diagnostic-badge">
      {!isOpen ? (
        <Badge
          variant={errorCount > 0 ? 'destructive' : warningCount > 0 ? 'secondary' : 'default'}
          className="cursor-pointer hover-elevate active-elevate-2"
          onClick={() => setIsOpen(true)}
          data-testid="button-open-diagnostic"
        >
          DEV {okCount}/{checks.length}
        </Badge>
      ) : (
        <Card className="w-96 max-h-96 overflow-auto">
          <CardHeader className="flex flex-row items-center justify-between gap-2 pb-2">
            <CardTitle className="text-sm font-medium">Development Diagnostics</CardTitle>
            <button
              onClick={() => setIsOpen(false)}
              className="hover-elevate active-elevate-2 rounded p-1"
              data-testid="button-close-diagnostic"
            >
              <ChevronUp className="w-4 h-4" />
            </button>
          </CardHeader>
          <CardContent className="space-y-2">
            {checks.map((check, index) => (
              <div
                key={index}
                className="flex items-center justify-between gap-2 text-sm"
                data-testid={`diagnostic-check-${index}`}
              >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  {getStatusIcon(check.status)}
                  <span className="font-mono text-xs truncate">{check.name}</span>
                </div>
                <span className="text-xs text-muted-foreground">{check.message}</span>
              </div>
            ))}

            {healthData && (
              <div className="mt-4 pt-4 border-t">
                <div className="text-xs font-mono space-y-1">
                  {healthData.version && (
                    <div>Version: {healthData.version}</div>
                  )}
                  {healthData.environment && (
                    <div>Env: {healthData.environment}</div>
                  )}
                  {healthData.timestamp && (
                    <div>Time: {new Date(healthData.timestamp).toLocaleTimeString()}</div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
